import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
/**
 * Slice 5 — admin upload pipeline.
 *
 * Two top-level Firestore collections drive Linda's Beheer Kennisbank UI:
 *
 *   `ingest_jobs/{id}` — short-lived job records. Created on upload-url
 *      handshake, finalized on file confirm, transitioned to running once
 *      the Cloud Run job picks up the work, terminal once chunking + embed
 *      completes (or fails). Polled by the UI for progress.
 *
 *   `kennisbank_ring1_documents/{filename}` — per-source-document registry
 *      with chunk count + last ingestedAt + last actor. Decouples "what
 *      Linda sees in the list" from a full chunk table scan.
 *
 * Both are documented exceptions to CLAUDE.md hard rule 1 (multi-tenant)
 * because Ring 1 is a single shared corpus across every tenant. Suppohrt
 * admin RBAC is enforced at every route.
 */
export const IngestJobAction = z.enum(['upload', 'reingest', 'delete']);
/**
 * Three-tier document corpus (Linda 2026-05-21). Picked at upload time
 * from the Beheer Kennisbank tab and persisted on the ingest job; the
 * per-doc Cloud Run ingest reads it to route chunks + registry doc to
 * the right collection pair. Move-between-tiers is a separate endpoint
 * (PATCH /:filename/tier) that re-locates the chunks + registry doc
 * without re-extracting / re-embedding.
 */
export const DocumentTier = z.enum(['ring1', 'ring0', 'ring_hq']);
/**
 * State machine, mirrored by `system_status/ingest_lock` for at-most-one
 * concurrency. Order: pending → staging → validated → invoking → running
 * → succeeded | failed | cancelled. Terminal states: succeeded, failed,
 * cancelled.
 */
export const IngestJobStatus = z.enum([
    'pending', // upload-url minted, awaiting client PUT
    'staging', // client uploaded, awaiting finalize
    'validated', // server-side validation passed (size, MIME, md5, pdf integrity)
    'invoking', // Cloud Run job invocation in flight (lock=acquiring)
    'running', // Cloud Run job started (lock=running, executionName populated)
    'succeeded',
    'failed',
    'cancelled',
]);
export const IngestJob = z.object({
    id: DocId,
    action: IngestJobAction,
    /** Source document filename. Lower-cased, no path. */
    filename: z.string().min(1).max(255),
    /** sha256 of canonical filename — keep it, it's an audit-log linkage. */
    filenameHash: z.string().regex(/^[a-f0-9]{64}$/),
    status: IngestJobStatus,
    /** Server-minted GCS path under the staging bucket. Only set for `upload`. */
    gcsStagingPath: z.string().optional(),
    /** Bytes — populated on staging from the upload-url request. */
    sizeBytes: z.number().int().nonnegative().optional(),
    /** Sniffed Content-Type — bound to the signed URL on `upload`. */
    contentType: z.string().max(120).optional(),
    /** md5 of the staged object, captured on finalize and verified server-side. */
    md5Base64: z.string().optional(),
    /** Long-running operation name from `jobs.run`. */
    operationName: z.string().optional(),
    /** Cloud Run execution resource name — populated by the job poller. */
    executionName: z.string().optional(),
    /** Last error code shown to the user (Dutch user-facing). */
    errorCode: z.string().optional(),
    /** Internal error trace — never returned to the client. */
    errorDetail: z.string().optional(),
    /**
     * Target tier for the doc. Set on `upload` and `reingest` from the
     * Beheer Kennisbank tab Linda was on when she dropped the file. The
     * per-doc Cloud Run ingest reads this and routes chunks + registry
     * to the right collection pair. Optional + defaults to 'ring1' for
     * backwards compatibility with pre-multi-tier jobs already in flight.
     */
    tier: DocumentTier.optional(),
    /** suppohrt_admin user id who kicked off the job. */
    actorUserId: DocId,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    /** When the staging signed URL expires. */
    stagingExpiresAt: IsoTimestamp.optional(),
});
/**
 * Querywaarde voor de map "Overig" op /documenten: documenten zonder
 * `documentType`. Een lege string kan dit niet uitdrukken, want die is op de
 * query niet te onderscheiden van "geen typefilter" — de gebruiker zou dan de
 * hele ring te zien krijgen onder een map die een handvol documenten belooft.
 * Bewust een waarde die geen legitiem documenttype kan zijn.
 */
export const UNTYPED_DOCUMENT_TYPE = '__zonder_type__';
export const DocumentRegistryEntry = z.object({
    /** Filename is the doc id — collision-safe across the flat corpus. */
    filename: z.string().min(1).max(255),
    documentTitle: z.string().max(500),
    author: z.string().max(255),
    /** Slice 6: optional secondary author (per-doc only, no cascade). */
    coAuthor: z.string().max(255).optional(),
    /**
     * Slice 6: per-doc metadata overrides. When `metadataOverridden` is true,
     * a future re-ingest preserves these values rather than recomputing from
     * the JSON author registry. Lookup priority at retrieval time: chunk row
     * (which already carries denormalized values populated from these
     * overrides) > registry. Editing any of these fields via PATCH flips
     * `metadataOverridden` to true.
     */
    institution: z.string().max(120).optional(),
    workingGroup: z.string().max(120).optional(),
    documentType: z.string().max(40).optional(),
    metadataOverridden: z.boolean().optional(),
    /** Number of chunks indexed in `kennisbank_ring1` for this filename. */
    chunkCount: z.number().int().nonnegative(),
    /**
     * When true, the source PDF/DOCX is in the canonical bucket and listed
     * in /documenten for download, but no chunks were indexed (typically
     * because OCR/extraction failed and the operator chose "Alleen als
     * download bewaren"). Retrieval is naturally blind to these (no chunks
     * to match), so the agent never cites them — the practitioner can still
     * find and download the original from /documenten with a clear
     * "Alleen download" badge so they know the assistant won't quote it.
     */
    documentOnly: z.boolean().optional(),
    /** Bytes of the source on GCS. */
    sizeBytes: z.number().int().nonnegative().optional(),
    contentType: z.string().max(120).optional(),
    /** Last successful ingest. Missing entries = never ingested. */
    lastIngestedAt: IsoTimestamp.optional(),
    lastActorUserId: DocId.optional(),
    /** Last job that touched this filename — for status-card linkage. */
    lastJobId: DocId.optional(),
    /** Tombstone: set by DELETE to suppress the filename from list responses. */
    deletedAt: IsoTimestamp.optional(),
    /**
     * Top-N high-level topics for the practitioner /documenten browse surface.
     * Auto-populated by the Python ingest's specialisation_tagger using the
     * same CANONICAL_SPECIALISATIONS list that powers the Sociale Kaart chip
     * rail and the SK Verrijking extractor — one vocabulary across all three
     * surfaces. Cap of 3 is a UI-density product call (table-cell chip rail
     * stays scannable). Linda inline-edits via Beheer Kennisbank when the
     * auto-tag gets it wrong (slice-6 EditableCell pattern).
     */
    specialisations: z.array(z.string().min(1).max(60)).max(3).optional(),
    /**
     * Trust tier for the chip rail on /documenten and the Beheer Kennisbank
     * admin list. Server-tagged at list time based on which Firestore
     * collection the doc came from:
     *   `kennisbank_ring1_documents` → 'ring1' (Kennisbank — clinical PDFs,
     *     visible to every authed user)
     *   `kennisbank_ring0_documents` → 'ring0' (Intern — internal SUPPOHRT
     *     staff only, isInternalRole gate)
     *   `kennisbank_ring_hq_documents` → 'ring_hq' (Kantoor — head-office
     *     only, isHeadOffice gate)
     * Defaults to 'ring1' — older clients + storage paths that predate the
     * multi-tier split simply behave as before.
     */
    ring: z.enum(['ring0', 'ring1', 'ring_hq']).optional(),
});
//# sourceMappingURL=ingest-job.js.map